import { useKycWorkflowKycSetting } from '@dealstack/frontend/app/pages/Project/MIP/hooks/useKycSetting';
import { INVESTOR_TEST_ELEMENT_ID } from '@dealstack/shared/tests/testElementIds';
import {
	Content,
	InvestmentContentWrapper,
	InvestmentHeader,
	Layout,
	MainLayout,
} from '@dealstack/frontend/investor/pages/Investment/common/investmentLayout';
import { MainHeaderBar } from '@dealstack/frontend/investor/pages/Investment/common/MainHeaderBar';
import { useInvestor } from '@dealstack/frontend/investor/pages/Investment/hooks/useInvestor';
import {
	REJECTED_FILES_ROUTES,
	RejectedFiles,
} from '@dealstack/frontend/investor/pages/Investment/InvestmentManage/RejectedFiles/RejectedFiles';
import { ROUTES } from '@dealstack/frontend/investor/pages/Routing/routes';
import { useGetInvestorKycForms } from '@dealstack/frontend/investor/pages/Investment/hooks/useGetInvestorKycForms';
import { hasFormAwaitingRequiredSignature } from '@dealstack/frontend/investor/pages/Investment/common/InheritedFormsConfirmation/InheritedFormsConfirmation.utils';
import { getVisibleCompletionSteps } from './InvestmentManage.utils';
import { Alert, AlertTitle } from '@dealstack/frontend/shared/components/Alert/Alert';
import { PrimarySideListV2 } from '@dealstack/frontend/shared/components/list/PrimarySideList/PrimarySideListV2';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import { getInvestmentDetails } from '@dealstack/frontend/shared/dataExchange/services/shareholder/investment.service';
import { useTranslationInvestor } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { SentryRoutes } from '@dealstack/frontend/shared/sentry';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams';
import {
	INVESTMENT_ROUTES,
	investmentFlowPath,
} from '@dealstack/frontend/investor/pages/Investment/Investment.routes';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n';
import {
	InvestmentWorkflowCompletionStep,
	PatchShareholderInvestorCompletionStepDto,
} from '@dealstack/shared/lib/generated';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { Button, Modal, Title } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { isNumber } from 'lodash';
import {
	useWizardStepRegistry,
	WizardStepRegistryProvider,
} from '@dealstack/frontend/shared/components/Wizard/WizardStepRegistry';
import { useEffect, useMemo, useState } from 'react';
import { Route, createSearchParams, useNavigate } from 'react-router-dom';
import { PrimarySideListV2Props } from '@dealstack/frontend/shared/components/list/PrimarySideList/PrimarySideListV2.types';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types';

export enum INVESTMENT_MANAGE_ROUTES {
	REJECTED_FILES = `/rejected-files`,
	MORE_DOCS_REQUESTED = `/more-docs-requested`,
}

interface SidebarConfigItem {
	key: number;
	title: string;
	route: string;
	id: PatchShareholderInvestorCompletionStepDto['step'] | 'REJECTED_FILES';
	subitems?: {
		key: number;
		title: string;
		route: string;
		id: PatchShareholderInvestorCompletionStepDto['step'];
	}[];
}

type InvestmentManageSidebarConfig = SidebarConfigItem[];

const getSideBarConfig = (
	t: TranslateFunction<I18Namespaces.INVESTOR>
): InvestmentManageSidebarConfig => [
	{
		title: t('investment.join.sidebar.steps.rejected_files'),
		route: INVESTMENT_MANAGE_ROUTES.REJECTED_FILES,
		id: 'REJECTED_FILES',
		key: 0,
		subitems: [
			{
				title: t('investment.personal_ids'),
				route: `${INVESTMENT_MANAGE_ROUTES.REJECTED_FILES}${REJECTED_FILES_ROUTES.PERSONAL}`,
				id: InvestmentWorkflowCompletionStep.KYC_PERSONAL,
				key: 1,
			},
			{
				title: t('investment.company_ids'),
				route: `${INVESTMENT_MANAGE_ROUTES.REJECTED_FILES}${REJECTED_FILES_ROUTES.COMPANY}`,
				id: InvestmentWorkflowCompletionStep.KYC_COMPANY,
				key: 2,
			},
			{
				title: t('investment.join.sidebar.steps.forms'),
				route: `${INVESTMENT_MANAGE_ROUTES.REJECTED_FILES}${REJECTED_FILES_ROUTES.KYC_FORMS}`,
				id: InvestmentWorkflowCompletionStep.KYC_FORMS,
				key: 3,
			},
			{
				title: t('investment.join.sidebar.steps.declarations'),
				route: `${INVESTMENT_MANAGE_ROUTES.REJECTED_FILES}${REJECTED_FILES_ROUTES.DECLARATIONS}`,
				id: InvestmentWorkflowCompletionStep.DECLARATIONS,
				key: 4,
			},
		],
	},
	{
		title: t('investment.join.sidebar.steps.more_documents_requested'),
		route: INVESTMENT_MANAGE_ROUTES.MORE_DOCS_REQUESTED,
		id: 'REJECTED_FILES',
		key: 5,
		subitems: [
			{
				title: t('investment.join.sidebar.steps.investment_vehicles'),
				route: `${INVESTMENT_MANAGE_ROUTES.MORE_DOCS_REQUESTED}${REJECTED_FILES_ROUTES.COMPANY}`,
				id: InvestmentWorkflowCompletionStep.OTHER,
				key: 6,
			},
		],
	},
];

const SIDEBAR_TOTAL_ITEMS = 6;

export const InvestmentManage = () => {
	const tInvestor = useTranslationInvestor();
	const [isContinueDisabled, setIsContinueDisabled] = useState(false);
	const { dealId, mipId, investmentWorkflowId } = useRequiredParams(
		'dealId',
		'mipId',
		'investmentWorkflowId'
	);
	const manageFlowPath = investmentFlowPath(INVESTMENT_ROUTES.MANAGE, {
		dealId,
		mipId,
		investmentWorkflowId,
	});

	const getInvestmentDetailsRequest = useQuery({
		queryKey: [
			API_PATHS.SHAREHOLDER_INVESTMENT_BY_MIP_ID_MIPID_GET,
			{ mipId, investmentWorkflowId },
		],
		queryFn: ({ signal }) => getInvestmentDetails({ mipId, investmentWorkflowId }, signal),
		enabled: !!mipId && !!investmentWorkflowId,
	});
	const getInvestorDataRequest = useInvestor();

	const navigate = useNavigate();
	const stepRegistry = useWizardStepRegistry();

	const [isLoading, setIsLoading] = useState(false);

	const investorId = getInvestorDataRequest?.data?.id;
	const investmentDetails = getInvestmentDetailsRequest.data?.payload;
	const { data: kycSetting } = useKycWorkflowKycSetting(
		investmentDetails?.activeKycWorkflowId ?? ''
	);

	const isPIVInvestment = !!investmentDetails?.isPiv;

	// A submitted-but-unsigned form sits at UPLOADED_NOT_VERIFIED — neither missing nor rejected —
	// which would hide the Forms sub-step and leave the investor with no way to sign.
	const { data: kycForms } = useGetInvestorKycForms(
		investmentDetails?.activeKycWorkflowId ?? '',
		{ enabled: !!investmentDetails?.activeKycWorkflowId }
	);
	const hasKycFormAwaitingSignature = hasFormAwaitingRequiredSignature(kycForms ?? []);

	const sideBarConfig = useMemo(() => {
		const {
			pivStatus,
			formStatus,
			proofOfAddressStatus,
			proofOfIdentityStatus,
			isDeclarationsRejected,
		} = investmentDetails || {};
		const config = getSideBarConfig(tInvestor);

		const visibleSteps: (SidebarConfigItem['id'] | '')[] = getVisibleCompletionSteps({
			proofOfIdentityStatus,
			proofOfAddressStatus,
			pivStatus,
			formStatus,
			isDeclarationsRejected,
			isPIVInvestment,
			hasKycFormAwaitingSignature,
		});

		return config
			.filter(
				(step) =>
					!!step.subitems?.filter((subStep) => visibleSteps.includes(subStep.id)).length
			)
			.map((step) => ({
				...step,
				subitems: step.subitems?.filter((subStep) => visibleSteps.includes(subStep.id)),
			}));
	}, [investmentDetails, isPIVInvestment, hasKycFormAwaitingSignature]);

	const activeMainStep = useMemo(
		() => sideBarConfig.find((step) => window.location.pathname.includes(step.route)),
		[window.location.pathname, sideBarConfig]
	);

	const activeSubStep = useMemo(
		() =>
			activeMainStep?.subitems?.find((subStep) =>
				window.location.pathname.includes(subStep.route)
			),
		[window.location.pathname, activeMainStep?.subitems]
	);

	const activeStepKey = useMemo(
		() => activeSubStep?.key || activeMainStep?.key,
		[activeMainStep, activeSubStep]
	);

	const steps = useMemo(
		() =>
			sideBarConfig.reduce((acc, current) => {
				return [...acc, current, ...(current.subitems || [])];
			}, [] as InvestmentManageSidebarConfig),
		[sideBarConfig]
	);

	const nextStep = useMemo(() => {
		if (!isNumber(activeStepKey)) {
			return;
		}

		let nextStepKey = activeStepKey + 1;
		let nextStep;

		while (nextStepKey <= SIDEBAR_TOTAL_ITEMS) {
			const step: SidebarConfigItem | undefined = steps.find(
				// eslint-disable-next-line no-loop-func
				(step) => step.key === nextStepKey
			);

			if (step && !step.subitems) {
				nextStep = step;
				break;
			}

			++nextStepKey;
		}

		return nextStep;
	}, [activeStepKey, steps]);

	useEffect(() => {
		if ((!activeMainStep || !activeSubStep) && steps.length > 0) {
			const firstStep: SidebarConfigItem = steps[0];
			const route = firstStep.subitems?.length
				? firstStep.subitems[0].route
				: firstStep.route;
			navigate(`${manageFlowPath}${route}`);
		}
	}, [activeSubStep, activeMainStep, steps, getInvestorDataRequest.isLoading]);

	const [isBackDisabled, setIsBackDisabled] = useState(true);

	const isLastStep = activeStepKey === steps[steps.length - 1]?.key;
	const [showFinishModal, setShowFinishModal] = useState(false);

	const onNextStep = async () => {
		if (isLastStep && activeSubStep?.id !== 'DECLARATIONS') {
			setShowFinishModal(true);
			return;
		}

		const result = await stepRegistry.saveActiveStep('next');
		if (!result.ok) {
			return;
		}
		if (isLastStep) {
			setShowFinishModal(true);
			return;
		}
		if (nextStep?.route) {
			navigate(`${manageFlowPath}${nextStep.route}`);
		}
	};

	const activeMainStepArrayIndex = useMemo(() => {
		return sideBarConfig?.findIndex((step) => {
			if (activeMainStep?.id) {
				return activeMainStep.id === step?.id;
			} else {
				return activeMainStep?.key === step?.key;
			}
		});
	}, [sideBarConfig, activeMainStep]);

	const activeSubStepArrayIndex = useMemo(() => {
		return activeMainStep?.subitems?.findIndex((subStep) => activeSubStep?.id === subStep?.id);
	}, [activeSubStep, activeMainStep]);

	const previousStep = useMemo(() => {
		if (!isNumber(activeStepKey)) {
			return;
		}

		let previousStepKey = activeStepKey - 1;
		let previousStep;

		while (previousStepKey > 0) {
			const step: SidebarConfigItem | undefined = steps.find(
				// eslint-disable-next-line no-loop-func
				(step) => step.key === previousStepKey
			);

			if (step && !step.subitems) {
				previousStep = step;
				break;
			}

			--previousStepKey;
		}

		return previousStep;
	}, [activeStepKey, steps, isLastStep, activeSubStep, nextStep, activeMainStep]);

	const onPreviousStep = async () => {
		if (!previousStep?.route) {
			return;
		}
		const result = await stepRegistry.saveActiveStep('back');
		if (!result.ok) {
			return;
		}
		navigate(`${manageFlowPath}${previousStep.route}`);
	};

	useEffect(() => {
		setIsBackDisabled(!activeStepKey || !previousStep?.route);
	}, [activeStepKey]);

	const onFinish = () => {
		if (investmentDetails) {
			const pathName = ROUTES.VALUATIONS.replace(
				':investorId',
				`${investmentDetails.investorId}`
			);
			navigate(pathName);
			return;
		}
		navigate({
			pathname: ROUTES.DASHBOARD,
			search: createSearchParams({
				mipId,
			}).toString(),
		});
	};

	useEffect(() => {
		if (!investmentDetails || !getInvestorDataRequest.data || steps.length > 0) {
			return;
		}
		onFinish();
	}, [investmentDetails, steps, getInvestorDataRequest.data]);

	const sideBarItems = useMemo((): PrimarySideListV2Props['sideListItems'] | undefined => {
		return sideBarConfig?.map((item) => ({
			key: item.key as number,
			title: item.title as string,
			subItems: item?.subitems?.map((subItem) => ({
				key: subItem?.key as number,
				title: subItem?.title as string,
			})),
		}));
	}, [sideBarConfig]);

	const redirectLink = investorId
		? ROUTES.VALUATIONS.replace(':investorId', `${investorId}`)
		: ROUTES.DASHBOARD.toString();

	return (
		<Layout>
			{isLoading ? <InlineLoader /> : null}
			<WizardStepRegistryProvider registry={stepRegistry}>
				<MainHeaderBar
					title={activeMainStep?.title}
					activeMainStepIndex={activeMainStepArrayIndex}
					activeSubStepIndex={activeSubStepArrayIndex}
					sidebarItemsCount={sideBarConfig?.length}
					onPrevStep={onPreviousStep}
					isPrevDisabled={isBackDisabled}
					onNextStep={onNextStep}
					isNextDisabled={isContinueDisabled}
					isLastStep={isLastStep}
					redirectLink={redirectLink}
				/>

				<MainLayout>
					<PrimarySideListV2
						sideListItems={sideBarItems ?? []}
						activeKey={activeStepKey}
					/>

					<Content>
						<InvestmentHeader>
							<Title order={5}>{activeSubStep?.title ?? activeMainStep?.title}</Title>
						</InvestmentHeader>

						<InvestmentContentWrapper>
							<SentryRoutes>
								<Route
									path={`${INVESTMENT_MANAGE_ROUTES.REJECTED_FILES}/*`}
									element={
										<RejectedFiles
											setIsContinueDisabled={setIsContinueDisabled}
											investmentDetails={investmentDetails}
											isPIVInvestment={isPIVInvestment}
											setIsLoading={setIsLoading}
											kycSetting={kycSetting}
										/>
									}
								/>
								<Route
									path={`${INVESTMENT_MANAGE_ROUTES.MORE_DOCS_REQUESTED}/*`}
									element={
										<RejectedFiles
											setIsContinueDisabled={setIsContinueDisabled}
											investmentDetails={investmentDetails}
											isPIVInvestment={isPIVInvestment}
											setIsLoading={setIsLoading}
											kycSetting={kycSetting}
										/>
									}
								/>
							</SentryRoutes>
						</InvestmentContentWrapper>
					</Content>
				</MainLayout>

				<Modal
					opened={showFinishModal}
					centered
					withCloseButton={false}
					onClose={onFinish}
					sx={{ '.mantine-Modal-body': { padding: 0 } }}
				>
					<Alert icon="tick-circle-filled" state="success" size="m">
						<AlertTitle>
							{tInvestor('investment.join.completed.modal.title')}
						</AlertTitle>

						<Button
							size="md"
							variant="secondary"
							onClick={onFinish}
							data-testid={
								INVESTOR_TEST_ELEMENT_ID.INVESTOR_MANAGE_FINISH_GO_TO_DASHBOARD_BUTTON
							}
						>
							{tInvestor('investment.join.completed.modal.go_to_dashboard')}
						</Button>
					</Alert>
				</Modal>
			</WizardStepRegistryProvider>
		</Layout>
	);
};
